interface UsageStep {
  step: string;
  label: string;
  desc: string;
}

interface Product {
  id: number;
  name: string;
  detail: {
    description: string;
    detailImage?: string;
    usage: string;
    usageSteps?: UsageStep[];
    caution: string;
    cautionList?: string[];
  };
}

interface Props {
  product: Product;
}

const ProductDetailInfo = ({ product }: Props) => {
  const { usageSteps, cautionList, detailImage, description } = product.detail;

  return (
    <section className="bg-white">
      {/* 제품 상세 이미지 영역 */}
      {detailImage && (
        <div className="py-10 px-4 md:px-10 max-w-7xl mx-auto">
          <div className="w-full">
            <img
              src={detailImage}
              alt={`${product.name} 제품 상세 이미지`}
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
        </div>
      )}

      {/* 텍스트 설명 (이미지 없을 때) */}
      {!detailImage && (
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-6 h-0.5 bg-[#1a2b4a]" />
            <span className="text-xs font-semibold text-[#1a2b4a] tracking-[0.2em] uppercase">제품 상세</span>
          </div>
          <p className="text-gray-500 leading-relaxed text-sm md:text-base">{description}</p>
        </div>
      )}

      {/* 복용법 & 주의사항 전용 섹션 */}
      <div className="bg-[#f8f9fb] border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">

          {/* 섹션 헤더 */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-6 h-0.5 bg-[#1a2b4a]" />
            <span className="text-xs font-semibold text-[#1a2b4a] tracking-[0.2em] uppercase">How to Use</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* 복용법 카드 */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 px-8 py-6 border-b border-gray-100 bg-[#1a2b4a]">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/15">
                  <i className="ri-capsule-line text-white text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">섭취 방법</h3>
                  <p className="text-white/60 text-xs mt-0.5">올바른 섭취로 효과를 높이세요</p>
                </div>
              </div>

              <div className="px-8 py-7">
                {/* 요약 */}
                <div className="flex items-start gap-3 mb-7 p-4 bg-[#1a2b4a]/5 rounded-xl">
                  <i className="ri-information-line text-[#1a2b4a] text-base mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#1a2b4a] font-medium leading-relaxed">{product.detail.usage}</p>
                </div>

                {/* 단계별 안내 */}
                {usageSteps && usageSteps.length > 0 && (
                  <div className="flex flex-col gap-4">
                    {usageSteps.map((s) => (
                      <div key={s.step} className="flex items-start gap-4">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1a2b4a] text-white text-xs font-bold flex-shrink-0 mt-0.5">
                          {s.step}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#1a2b4a] mb-1">{s.label}</p>
                          <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 주의사항 카드 */}
            <div className="bg-white rounded-2xl border border-amber-100 overflow-hidden">
              <div className="flex items-center gap-3 px-8 py-6 border-b border-amber-100 bg-amber-50">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-100">
                  <i className="ri-alert-line text-amber-600 text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-amber-800">주의 사항</h3>
                  <p className="text-amber-600/70 text-xs mt-0.5">섭취 전 반드시 확인하세요</p>
                </div>
              </div>

              <div className="px-8 py-7">
                {cautionList && cautionList.length > 0 ? (
                  <ul className="flex flex-col gap-4">
                    {cautionList.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0 mt-0.5">
                          <i className="ri-error-warning-line text-amber-600 text-xs" />
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{item}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-amber-700/80 leading-relaxed">{product.detail.caution}</p>
                )}

                {/* 공통 면책 문구 */}
                <div className="mt-6 pt-5 border-t border-amber-100">
                  <div className="flex items-start gap-2">
                    <i className="ri-stethoscope-line text-amber-500 text-sm mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-600/80 leading-relaxed">
                      본 제품은 의약품이 아니므로 질병의 예방 및 치료를 위한 의약품을 대체할 수 없습니다. 특이 체질이거나 지병이 있는 경우 섭취 전 의사와 상담하시기 바랍니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* 보관 방법 공통 안내 */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: 'ri-temp-cold-line', title: '보관 온도', desc: '직사광선을 피해 서늘하고 건조한 곳에 보관하세요.' },
              { icon: 'ri-shield-check-line', title: '어린이 안전', desc: '어린이의 손이 닿지 않는 곳에 보관하세요.' },
              { icon: 'ri-calendar-check-line', title: '유통기한', desc: '유통기한을 확인하고 기한 내에 섭취하세요.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 bg-white rounded-xl px-6 py-5 border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#1a2b4a]/8 flex-shrink-0">
                  <i className={`${item.icon} text-[#1a2b4a] text-base`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1a2b4a] mb-1">{item.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default ProductDetailInfo;
